/**
 * KAN-815: action on the next bot touch of a client: send it now (KAN-816:
 * no cancel, manual mode covers it). After the request (also after a 409: the touch may have changed) the
 * attention block (with the nested CRM board key), the client card and the
 * client's chat feed are refreshed; a 409 stays in mutation.error for the note.
 * The send-now result is a toast fired here, so it stays visible when the
 * note unmounts (the touch is gone after the refresh).
 * KAN-817: draft generates the reminder text without sending it; the result
 * (the touch with its draft) stays in draft.data, refreshed the same way.
 * KAN-858: cancel ("Не писать") removes the touch; offered only where the
 * caller asks for it (the manager app), refreshed the same way.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientsApi } from '@/services/api/clients';
import { clientQueryKey } from '@/services/api/clientQueries';
import { ATTENTION_QUERY_KEY } from '@/services/api/queryKeys';
import { transcriptQueryKey } from '@/components/clients/useTranscriptMessages';
import { SEND_NOW_RESULT_KIND, SEND_NOW_RESULT_TEXT } from '@/lib/nextBotTouch';

export const CANCEL_DONE_TEXT = 'Бот не напишет это напоминание';

export function useNextBotTouchActions(clientId: string) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ATTENTION_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: clientQueryKey(clientId) });
    void queryClient.invalidateQueries({ queryKey: transcriptQueryKey(clientId, null) });
  };
  const sendNow = useMutation({
    mutationFn: () => clientsApi.sendNextBotTouchNow(clientId),
    onSuccess: (data) => {
      const text = SEND_NOW_RESULT_TEXT[data.status];
      if (text) toast[SEND_NOW_RESULT_KIND[data.status]](text);
    },
    onSettled: refresh,
  });
  const draft = useMutation({
    mutationFn: () => clientsApi.draftNextBotTouch(clientId),
    onSettled: refresh,
  });
  const cancel = useMutation({
    mutationFn: () => clientsApi.cancelNextBotTouch(clientId),
    onSuccess: () => toast.success(CANCEL_DONE_TEXT),
    onSettled: refresh,
  });
  return { sendNow, draft, cancel };
}
